// Contracting API surface for the operator SPA: SOW versions, clauses, change requests,
// baselines and the payment-schedule milestones. Every call goes through the shared request
// helper — never raw fetch in components.
import { request } from './client.js';
import type { Degradable } from '../../shared/problems.js';

// ───────────────────────── types ─────────────────────────

export type SowStatus = 'draft' | 'issued' | 'signed' | 'superseded';
export type CrStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'deferred' | 'withdrawn';
export type MilestoneStatus = 'planned' | 'in_progress' | 'delivered' | 'accepted' | 'invoiced';

export interface SowVariableDef {
  key: string;
  label: string;
  kind: 'text' | 'longtext' | 'date' | 'money';
  group: 'commercial' | 'schedule' | 'parties' | 'narrative';
  hint?: string;
  default?: string;
}

export interface SowSection {
  key: string;
  title: string;
  overridden: boolean;
}

export interface PdfStatus {
  available: boolean;
  binary: string;
  reason: string | null;
  generated?: boolean;
}

export interface ContractingMeta {
  variables: SowVariableDef[];
  sections: { key: string; title: string }[];
  clause_section_prefix: string;
  clauses: { key: string; title: string; version: number }[];
  pdf: PdfStatus;
}

export interface ClausePin {
  key: string;
  version: number;
}

export interface SowSignature {
  id?: number;
  typed_name: string;
  affirmation: string;
  signed_at: string;
  content_hash: string;
  ip_address?: string | null;
  user_agent?: string | null;
  portal_user_name?: string | null;
  operator_name?: string | null;
  portal_user?: string | null;
  operator?: string | null;
}

export interface SowVersionRow {
  id: number;
  sow_id: number;
  version_no: number;
  status: SowStatus;
  is_addendum: boolean;
  content_hash: string | null;
  issued_at: string | null;
  signed_at: string | null;
  published: boolean;
  created_at: string;
  has_pdf: boolean;
  has_html: boolean;
  variable_values: Record<string, string>;
  clause_versions: ClausePin[];
  override_count: number;
  baseline_id: number | null;
  baseline_hash: string | null;
  signature: SowSignature | null;
}

export interface LintFailure {
  ac_ref: string;
  requirement_ref: string;
  statement: string;
  errors: string[];
}

/** What issuing this draft would actually do — the hash the client signs, not the draft's. */
export interface IssueCheck {
  version_id: number;
  version_no: number;
  status: SowStatus;
  draft_hash: string | null;
  issued_hash: string;
  hash_changes: boolean;
  register_snapshot_changed: boolean;
  lint_failures: LintFailure[];
  lint_failure_count: number;
  totals: Record<string, number>;
}

export interface SowVersionDetail extends Omit<SowVersionRow, 'override_count' | 'baseline_id' | 'baseline_hash' | 'signature'> {
  section_overrides: Record<string, string>;
  rendered_html: string | null;
  pdf_key: string | null;
  engagement_id: number;
  sow_title: string;
  engagement_code: string;
  engagement_name: string;
  sections: SowSection[];
  signatures?: SowSignature[];
  baseline?: { id: number; label: string | null; content_hash: string; created_at: string } | null;
  pdf?: PdfStatus;
  warnings?: string[];
  notice?: string;
  versions_superseded?: number;
  register_snapshot_changed?: boolean;
  unverifiable_acs_accepted?: string[];
}

export interface Sow {
  id: number;
  engagement_id: number;
  title: string;
  created_at: string;
  versions: SowVersionRow[];
}

export interface ClauseVersion {
  id: number;
  key: string;
  title: string;
  body_md: string;
  version: number;
  active: boolean;
  created_at: string;
}

export interface ClauseEntry {
  key: string;
  title: string;
  active: boolean;
  latest: ClauseVersion;
  versions: ClauseVersion[];
}

export interface ClauseUsage {
  key: string;
  latest_version: number | null;
  total: number;
  stale: number;
  signed_on_old_version: number;
  rows: {
    engagement_id: number;
    engagement_code: string;
    engagement_name: string;
    client_name: string;
    sow_version_id: number;
    version_no: number;
    status: SowStatus;
    signed_at: string | null;
    issued_at: string | null;
    clause_version: number;
    stale: boolean;
  }[];
}

export interface ChangeRequest {
  id: number;
  engagement_id: number;
  ref: string;
  title: string;
  description: string | null;
  origin: string | null;
  origin_document_id: number | null;
  origin_document_ref: string | null;
  impact_cost: string | null;
  impact_days: string | null;
  impact_risk: string | null;
  goodwill: boolean;
  status: CrStatus;
  decided_at: string | null;
  decided_by_name: string | null;
  decided_by_portal_name: string | null;
  published: boolean;
  created_at: string;
  requirement_refs: string[];
  revision_count: number;
}

export interface ChangeRequestDetail extends ChangeRequest {
  revisions: {
    id: number;
    revision_no: number;
    statement: string;
    rationale: string | null;
    created_at: string;
    created_by_kind: string;
    created_by_name: string | null;
    requirement_id: number;
    requirement_ref: string;
    requirement_status: string;
    previous_statement: string | null;
  }[];
  comments: { id: number; body: string; created_at: string; portal_user_name: string | null; operator_name: string | null }[];
}

export interface RevisionInstruction {
  action: 'modify' | 'create' | 'descope';
  requirement_id?: number;
  statement?: string;
  rationale?: string;
  type?: string;
  priority?: string;
  category?: string;
  reason?: string;
}

export interface BaselineRow {
  id: number;
  label: string | null;
  content_hash: string;
  created_at: string;
  sow_version_id: number;
  version_no: number | null;
  is_addendum: boolean | null;
  signed_at: string | null;
  sow_title: string | null;
  requirement_count: number;
  deliverable_count: number;
}

export interface FieldChange {
  field: string;
  before: unknown;
  after: unknown;
}
export interface DiffEntry {
  ref: string;
  label: string;
  changes: FieldChange[];
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}
export interface DiffGroup {
  added: DiffEntry[];
  removed: DiffEntry[];
  changed: DiffEntry[];
  unchanged: number;
}
export interface SnapshotHead {
  kind: 'baseline' | 'live';
  id: number | null;
  label: string;
  created_at: string | null;
  content_hash: string | null;
  counts: Record<string, number>;
}
export interface BaselineDiff {
  engagement_id: number;
  from: SnapshotHead;
  to: SnapshotHead;
  requirements: DiffGroup;
  deliverables: DiffGroup;
  acceptance_criteria: DiffGroup;
  milestones: DiffGroup;
  summary: { added: number; removed: number; changed: number; identical: boolean };
}

export interface Milestone {
  id: number;
  name: string;
  due_date: string | null;
  amount: string | null;
  status: MilestoneStatus;
  sort: number;
  published: boolean;
  created_at: string;
  deliverable_count: number;
  acceptance_record_count: number;
}

/**
 * The answer to "may this go, what goes with it, and if not why not" — the wire shape of the
 * server's utils/deletion.ts DeletionPlan. A blocker always names the alternative, so a blocked
 * delete can show the operator what to do instead of a dead button.
 */
export interface DeletionBlocker {
  code: string;
  message: string;
}
export interface DeletionPlan {
  allowed: boolean;
  blockers: DeletionBlocker[];
  cascade: { what: string; count: number }[];
  label: string;
}

// ───────────────────────── readiness (the pre-flight) ─────────────────────────

/** A value the RECORD holds for a blank contract variable. `source_label` names its column. */
export interface ReadinessCandidate {
  key: string;
  value: string;
  source_label: string;
}

export interface ReadinessMilestone {
  id: number;
  name: string;
  due_date: string | null;
  amount: string | null;
  status: string;
  published: boolean;
  deliverable_count: number;
  acceptance_record_count: number;
}

export interface ReadinessRequirement {
  ref: string;
  type: string;
  priority: string;
  status: string;
  statement: string;
}

export interface ReadinessProjection {
  in_scope: number;
  must: number;
  out_of_scope: number;
  deliverables: number;
  milestones: number;
  planned_value: number;
  assumptions: number;
  constraints: number;
  register_rows: number;
  acceptance_criteria: number;
  scope_groups: { category: string; count: number }[];
}

/**
 * Is this engagement fit to print a contract, and what is stopping it — answered BEFORE a
 * version exists. Mirrors src/server/routes/contractingReadiness.ts; the lint failures are the
 * same walk `issue-check` does, so the pre-flight zone and the issue dialog cannot disagree.
 */
export interface ContractingReadiness extends Degradable {
  engagement_id: number;
  currency: string;
  estimate_value: number | null;
  planned_value: number;
  review_window_days: number | null;
  projection: ReadinessProjection;
  lint_failures: LintFailure[];
  lint_failure_count: number;
  unfunded_milestones: ReadinessMilestone[];
  blank_variables: string[];
  candidates: ReadinessCandidate[];
  draft_requirements: number;
  draft_requirement_refs: string[];
  requirements: ReadinessRequirement[];
  gate: {
    gate: string;
    title: string;
    conditions: { label: string; kind: 'auto' | 'manual'; met: boolean; detail: string }[];
    metCount: number;
    total: number;
  } | null;
  parties: {
    client_name: string;
    legal_name: string | null;
    city: string | null;
    country: string | null;
    sponsor_name: string | null;
    sponsor_title: string | null;
  };
}

// ───────────────────────── calls ─────────────────────────

const qs = (params: Record<string, string | number | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') search.set(k, String(v));
  const s = search.toString();
  return s ? `?${s}` : '';
};

/** A register requirement as the SOW renderer sees it. `id` is what the inline edits write to. */
export interface SowDocRow {
  id: number;
  ref: string;
  type: string;
  priority: string;
  status: string;
  category: string | null;
  statement: string | null;
  rationale: string | null;
  [k: string]: unknown;
}

/** The live register in SOW structure — what GET /contracting/register-document returns. */
export interface SowDocumentContext {
  engagement_id: number;
  generated_at: string;
  engagement: Record<string, unknown>;
  client: Record<string, unknown>;
  sponsor: Record<string, unknown> | null;
  currency: string;
  variables: Record<string, string>;
  scope_groups: { category: string; rows: SowDocRow[] }[];
  out_of_scope: SowDocRow[];
  assumptions: SowDocRow[];
  constraints: SowDocRow[];
  deliverables: Record<string, unknown>[];
  acceptance_criteria: { requirement_ref: string; requirement_statement: string; rows: Record<string, unknown>[] }[];
  milestones: Record<string, unknown>[];
  totals: { in_scope: number; must: number; out_of_scope: number; deliverables: number; milestones: number; planned_value: number };
}

export const contractingApi = {
  meta: () => request<ContractingMeta>('/contracting/meta'),
  /** The live register rendered into SOW sections (rows carry ids) — the Scope document page. */
  registerDocument: (engagementId: number) =>
    request<SowDocumentContext>(`/contracting/register-document${qs({ engagement_id: engagementId })}`),
  /** The printable traceability document (A4 landscape sheets). `print` opens the dialog on load. */
  traceabilityHtmlUrl: (engagementId: number, print = true) =>
    `/api/contracting/traceability.html${qs({ engagement_id: engagementId, print: print ? 1 : undefined })}`,

  /** The pre-flight: what a contract would print today, and what the server would refuse. */
  readiness: (engagementId: number) =>
    request<ContractingReadiness>(`/contracting/readiness${qs({ engagement_id: engagementId })}`),

  // ---- SOW ----
  sows: (engagementId: number) =>
    request<{ engagement_id: number; pdf: PdfStatus; rows: Sow[] }>(`/contracting/sows${qs({ engagement_id: engagementId })}`),
  createSow: (engagementId: number, title?: string) =>
    request<Sow>('/contracting/sows', { method: 'POST', body: JSON.stringify({ engagement_id: engagementId, title }) }),
  createVersion: (
    sowId: number,
    body: { variable_values?: Record<string, string>; clause_keys?: string[]; is_addendum?: boolean; change_request_id?: number; carry_overrides?: boolean } = {},
  ) => request<SowVersionDetail>(`/contracting/sows/${sowId}/versions`, { method: 'POST', body: JSON.stringify(body) }),
  version: (id: number) => request<SowVersionDetail>(`/contracting/sow-versions/${id}`),
  updateVersion: (
    id: number,
    body: { variable_values?: Record<string, string>; clause_keys?: string[]; section_overrides?: Record<string, string>; repin_clauses?: boolean },
  ) => request<SowVersionDetail>(`/contracting/sow-versions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  issueCheck: (id: number) => request<IssueCheck>(`/contracting/sow-versions/${id}/issue-check`),
  issueVersion: (id: number, body: { confirm_unverifiable_acs?: boolean } = {}) =>
    request<SowVersionDetail>(`/contracting/sow-versions/${id}/issue`, { method: 'POST', body: JSON.stringify(body) }),
  markSigned: (id: number, body: { typed_name: string; affirmation: boolean; signed_on?: string }) =>
    request<SowVersionDetail>(`/contracting/sow-versions/${id}/mark-signed`, { method: 'POST', body: JSON.stringify(body) }),
  /** What discarding this version would do — drafts go, anything issued is superseded instead. */
  versionDeletionPlan: (id: number) => request<DeletionPlan>(`/contracting/sow-versions/${id}/deletion-plan`),
  deleteVersion: (id: number) => request<void>(`/contracting/sow-versions/${id}`, { method: 'DELETE' }),
  regeneratePdf: (id: number) => request<{ pdf_key: string; pdf: PdfStatus }>(`/contracting/sow-versions/${id}/pdf`, { method: 'POST' }),
  pdfUrl: (id: number) => `/api/contracting/sow-versions/${id}/pdf`,
  htmlUrl: (id: number) => `/api/contracting/sow-versions/${id}/html`,
  /** What the client would receive: rendered without the draft marker or the lint notes. */
  issuedHtmlUrl: (id: number) => `/api/contracting/sow-versions/${id}/html?as=issued`,
  registerPreview: (engagementId: number) =>
    request<{
      totals: { in_scope: number; must: number; out_of_scope: number; deliverables: number; milestones: number; planned_value: number };
      scope_groups: { category: string; count: number }[];
      out_of_scope: number; assumptions: number; constraints: number; milestones: number; register_rows: number;
    }>(`/contracting/register-preview${qs({ engagement_id: engagementId })}`),

  // ---- Clauses ----
  clauses: () => request<{ count: number; rows: ClauseEntry[] }>('/contracting/clauses'),
  createClause: (body: { key: string; title: string; body_md: string }) =>
    request<ClauseVersion>('/contracting/clauses', { method: 'POST', body: JSON.stringify(body) }),
  reviseClause: (key: string, body: { title: string; body_md: string }) =>
    request<ClauseVersion>(`/contracting/clauses/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify(body) }),
  setClauseActive: (key: string, active: boolean) =>
    request<{ key: string; active: boolean; versions_affected: number }>(`/contracting/clauses/${encodeURIComponent(key)}`, {
      method: 'PATCH', body: JSON.stringify({ active }),
    }),
  clauseUsage: (key: string) => request<ClauseUsage>(`/contracting/clauses/${encodeURIComponent(key)}/usage`),

  // ---- Change requests ----
  changeRequests: (engagementId: number, status?: CrStatus) =>
    request<{ count: number; currency: string; totals: Record<string, number>; rows: ChangeRequest[] }>(
      `/contracting/change-requests${qs({ engagement_id: engagementId, status })}`,
    ),
  changeRequest: (id: number) => request<ChangeRequestDetail>(`/contracting/change-requests/${id}`),
  createChangeRequest: (body: Record<string, unknown>) =>
    request<ChangeRequest>('/contracting/change-requests', { method: 'POST', body: JSON.stringify(body) }),
  updateChangeRequest: (id: number, body: Record<string, unknown>) =>
    request<ChangeRequest>(`/contracting/change-requests/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  setChangeRequestStatus: (id: number, status: CrStatus, note?: string, opts: { acknowledge_uncosted?: boolean } = {}) =>
    request<ChangeRequest>(`/contracting/change-requests/${id}/status`, {
      method: 'POST', body: JSON.stringify({ status, note, ...opts }),
    }),
  approveChangeRequest: (
    id: number,
    body: { revisions: RevisionInstruction[]; goodwill?: boolean; create_addendum?: boolean; sow_id?: number },
  ) =>
    request<{
      cr: ChangeRequest;
      applied: Record<string, unknown>[];
      addendum: { id: number; version_no: number } | null;
    }>(`/contracting/change-requests/${id}/approve`, { method: 'POST', body: JSON.stringify(body) }),
  /** What deleting this CR would take with it — refused once it has revised the register. */
  changeRequestDeletionPlan: (id: number) => request<DeletionPlan>(`/contracting/change-requests/${id}/deletion-plan`),
  deleteChangeRequest: (id: number) => request<void>(`/contracting/change-requests/${id}`, { method: 'DELETE' }),

  // ---- Baselines ----
  baselines: (engagementId: number) =>
    request<{ count: number; rows: BaselineRow[] }>(`/contracting/baselines${qs({ engagement_id: engagementId })}`),
  baseline: (id: number) => request<Record<string, unknown>>(`/contracting/baselines/${id}`),
  diff: (engagementId: number, from?: string, to?: string) =>
    request<BaselineDiff>(`/contracting/baseline-diff${qs({ engagement_id: engagementId, from, to })}`),
  diffHtmlUrl: (engagementId: number, from?: string, to?: string) =>
    `/api/contracting/baseline-diff.html${qs({ engagement_id: engagementId, from, to })}`,

  // ---- Milestones (payment schedule) ----
  milestones: (engagementId: number) =>
    request<{ count: number; currency: string; planned_value: number; rows: Milestone[] }>(
      `/contracting/milestones${qs({ engagement_id: engagementId })}`,
    ),
  createMilestone: (body: Record<string, unknown>) =>
    request<Milestone>('/contracting/milestones', { method: 'POST', body: JSON.stringify(body) }),
  updateMilestone: (id: number, body: Record<string, unknown>) =>
    request<Milestone>(`/contracting/milestones/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  /** What deleting this milestone would take with it — refused once it is in the signed record. */
  milestoneDeletionPlan: (id: number) => request<DeletionPlan>(`/contracting/milestones/${id}/deletion-plan`),
  deleteMilestone: (id: number) => request<void>(`/contracting/milestones/${id}`, { method: 'DELETE' }),
  reorderMilestones: (engagementId: number, ids: number[]) =>
    request<{ rows: Milestone[] }>('/contracting/milestones/reorder', {
      method: 'POST', body: JSON.stringify({ engagement_id: engagementId, ids }),
    }),
};
